import React, { Fragment, useState } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  CardBody,
  CardHeader,
  Alert
} from "reactstrap";
import moment from "moment";
import parse from "html-react-parser";
import { switchTrabajandoPublicationState } from "./Axios";
import Swal from "sweetalert2";
import { drawAasmState } from "../commons/Functions";

const getTextLength = html => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return tempDiv.textContent.trim().length;
};

const PublicationShow = props => {
  const { course, trabajando_publication, isPreview } = props;
  const { minisitePublication, trabajandoPublication } = course;
  const [aasmState, setAasmState] = useState(
    props.aasm_state || trabajandoPublication.aasmState
  );

  const neverPublished = props.never_published;
  const requiresRemoteUpdate = props.requires_remote_update;

  const descriptionRaw = isPreview
    ? course.jobDescription.value
    : course.jobDescription;
  const description = parse(descriptionRaw);

  const requirement = isPreview
    ? parse(trabajandoPublication.requirements?.value)
    : parse(trabajandoPublication.requirements);

  const benefits = isPreview
    ? parse(trabajandoPublication.benefits?.value)
    : parse(trabajandoPublication.benefits);

  const isConfidential = isPreview
    ? trabajandoPublication.isConfidential?.value
    : trabajandoPublication.isConfidential;

  const descriptionLength = getTextLength(descriptionRaw);
  const isDescriptionShort = descriptionLength < 150;

  // Acción: Publicar en Trabajando o volver a activar
  const handlePublishToTrabajando = () => {
    Swal.fire({
      title: "¿Estás seguro?",
      html: `La publicación será enviada a <b>Trabajando.cl</b> y quedará publicada.<br /><br />`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, publicar",
      cancelButtonText: "Cancelar"
    }).then(result => {
      if (result.value) {
        const formData = new FormData();
        formData.append("switch_state", "activate");
        switchTrabajandoPublicationState(
          trabajandoPublication.courseId,
          trabajandoPublication.id,
          formData,
          (response) => {
            if (response.status === 200) {
              Swal.fire("¡Publicado!", "La publicación ya está activa en Trabajando.", "success").then(() => {
                window.location.reload(); // reload aquí
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: response.data?.error || "No tienes permisos para realizar esta acción."
              });
            }
          }
        );
      }
    });
  };

  const handleCloseTrabajandoPublication = () => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: "La publicación se va a cerrar en Trabajando.cl y localmente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, cerrar",
      cancelButtonText: "Cancelar"
    }).then(result => {
      if (result.value) {
        const formData = new FormData();
        formData.append("switch_state", "deactivate");
        switchTrabajandoPublicationState(
          trabajandoPublication.courseId,
          trabajandoPublication.id,
          formData,
          (response) => {
            if (response.status === 200) {
              Swal.fire("¡Cerrada!", "La publicación se ha cerrado exitosamente.", "success").then(() => {
                window.location.reload(); // reload aquí
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: response.data?.error || "No tienes permisos para realizar esta acción."
              });
            }
          }
        );
      }
    });
  };

  const renderButtons = () => {
    const botones = [];

    botones.push(
      <Button key="ver_proceso" color="primary" className="mb-5" href={`/courses/${course.id}`}>
        Ver proceso
      </Button>
    );

    if (isDescriptionShort) {
      botones.push(
        <Button
          key="editar_descripcion"
          color="warning"
          className="mb-5 ms-2"
          href={`/courses/${course.id}/edit`}
          title="Editar descripción en la publicación original"
        >
          Editar descripción en publicación original
        </Button>
      );
    }

    if (["in_draft", "inactive"].includes(aasmState) && !isDescriptionShort) {
      if (neverPublished) {
        botones.push(
          <Button
            key="publicar"
            color="success"
            className="mb-5 ms-2"
            onClick={handlePublishToTrabajando}
          >
            Publicar en Trabajando
          </Button>
        );
      } else if (requiresRemoteUpdate) {
        botones.push(
          <Button
            key="actualizar"
            color="success"
            className="mb-5 ms-2"
            onClick={handlePublishToTrabajando}
          >
            Actualizar cambios en Trabajando
          </Button>
        );
      } else if (aasmState === "inactive") {
        botones.push(
          <Button
            key="reactivar"
            color="success"
            className="mb-5 ms-2"
            onClick={handlePublishToTrabajando}
          >
            Reactivar publicación en Trabajando
          </Button>
        );
      }

      botones.push(
        <Button
          key="editar_publicacion"
          color="secondary"
          className="mb-5 ms-2"
          href={`/courses/${course.id}/trabajando_publications/${trabajandoPublication.id}/edit`}
          title="Editar publicación en Trabajando"
        >
          Editar publicación en Trabajando
        </Button>
      );

      botones.push(
        <div key="alert_edit" className="d-inline-block align-middle ms-3" style={{ minWidth: 320 }}>
          <Alert color="info" className="mb-0 py-2 px-3">
            <i className="fas fa-info-circle me-2"></i>
            {aasmState === "inactive"
              ? "La publicación está cerrada. Puedes editarla y volver a publicarla cuando lo necesites."
              : "La publicación aún no ha sido publicada en Trabajando.cl. Puedes editarla antes de publicarla."
            }
          </Alert>
        </div>
      );
    }

    if (aasmState === "active") {
      botones.push(
        <Button
          key="cerrar_publicacion"
          color="danger"
          className="mb-5 ms-2"
          onClick={handleCloseTrabajandoPublication}
        >
          Cerrar publicación en Trabajando
        </Button>
      );
    }

    return botones;
  };

  return (
    <div>
      <Card className="mb-4 publication-full-card shadow-lg border-0 rounded-4 overflow-hidden" id="publication_show_info">
        <CardHeader className="p-4 shadow-sm border-bottom-0">
          <Row>
            <Col lg="8" md="7">
              <h2 className="fs-4 fw-bold mb-3" id="publication_title">
                {isPreview ? trabajandoPublication.title?.value : trabajandoPublication.title}
              </h2>
              <div className="media mb-4 mb-md-0 d-flex align-items-start flex-wrap">
                <div className="media-body">
                  <ul className="fa-ul list-unstyled mb-0 ms-4 publication-detail-list" id="publication-detail-list">
                    <li>
                      <span className="fa-li">
                        <i className="far fa-calendar-alt" title="Fecha de Publicación"></i>
                      </span>
                      Fecha creación:{" "}
                      {isPreview
                        ? moment().format("DD/MM/YYYY")
                        : moment(trabajandoPublication.createdAt).format("DD/MM/YYYY")}
                    </li>
                    <li>
                      <span className="fa-li">
                        <i className="fas fa-globe" title="Id Trabajando"></i>
                      </span>
                      ID trabajando:{" "}
                      {isPreview
                        ? trabajandoPublication.trabajandoId?.value
                        : trabajandoPublication.trabajandoId}
                    </li>
                    {(isPreview
                      ? !!trabajandoPublication.trabajandoId?.value
                      : !!trabajandoPublication.trabajandoId) ? (
                      <a
                        href={`https://www.trabajando.cl/trabajos-banco-bice-777/trabajo/${
                          isPreview
                            ? trabajandoPublication.trabajandoId?.value
                            : trabajandoPublication.trabajandoId
                        }-${trabajando_publication.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver publicación en Trabajando
                      </a>
                    ) : (
                      <span>No publicado</span>
                    )}
                    <li>
                      <span className="fa-li">
                        <i className="fas fa-business-time" title="Jornada, Horarios y/o Turnos"></i>
                      </span>
                      {!isPreview
                        ? `${trabajandoPublication.preferredSchedule
                          ? I18n.t("activerecord.enums.personal/laboral_requirement.preferred_schedules." +
                            trabajandoPublication.preferredSchedule)
                          : ""
                        }. ${trabajandoPublication.preferredLaborDay
                          ? I18n.t("activerecord.enums.personal/laboral_requirement.preferred_labor_days." +
                            trabajandoPublication.preferredLaborDay)
                          : ""
                        }. ${trabajandoPublication.preferredTurn
                          ? I18n.t("activerecord.enums.personal/laboral_requirement.preferred_turns." +
                            trabajandoPublication.preferredTurn)
                          : ""
                        }`
                        : `${trabajandoPublication.preferredSchedule?.value
                          ? I18n.t("activerecord.enums.personal/laboral_requirement.preferred_schedules." +
                            trabajandoPublication.preferredSchedule.value)
                          : ""
                        }. ${trabajandoPublication.preferredLaborDay?.value
                          ? I18n.t("activerecord.enums.personal/laboral_requirement.preferred_labor_days." +
                            trabajandoPublication.preferredLaborDay.value)
                          : ""
                        }. ${trabajandoPublication.preferredTurn?.value
                          ? I18n.t("activerecord.enums.personal/laboral_requirement.preferred_turns." +
                            trabajandoPublication.preferredTurn.value)
                          : ""
                        }.`}
                    </li>
                    {minisitePublication?.dressCode && (
                      <li>
                        <span className="fa-li">
                          <i className="fas fa-user-tie"></i>
                        </span>
                        {!isPreview
                          ? minisitePublication.dressCode
                            ? I18n.t("activerecord.enums.personal/laboral_requirement.dress_code." +
                              minisitePublication.dressCode)
                            : ""
                          : minisitePublication.dressCode?.value
                            ? I18n.t("activerecord.enums.personal/laboral_requirement.dress_code." +
                              minisitePublication.dressCode.value)
                            : ""}
                      </li>
                    )}
                    <li>
                      <span className="fa-li">
                        <i className="fas fa-users" title="Vacantes"></i>
                      </span>
                      {isPreview ? course.vacancies?.value : course.vacancies} Vacantes
                    </li>
                    {!isConfidential && (
                      <li>
                        <span className="fa-li">
                          <i className="fas fa-map-marker-alt" title="Ubicación"></i>
                        </span>
                        {!isPreview
                          ? `${trabajandoPublication.address}. ${trabajandoPublication.commune?.name}, ${trabajandoPublication.region?.name}, ${trabajandoPublication.country?.name}`
                          : `${trabajandoPublication.address?.value}. ${trabajandoPublication.commune?.label}, ${trabajandoPublication.region?.label}, ${trabajandoPublication.country?.label}`}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </Col>
            <Col lg="4" md="5" className="text-right rs_skip_always">
              <div className="d-flex justify-content-end align-items-center my-2">
                <h5>{drawAasmState(aasmState)}</h5>
              </div>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          <Row>
            <Col lg="12" md="12">
              <div className="publication_description mb-4" id="publication_description">
                <small className="d-block text-muted fw-bold">
                  Descripción
                </small>
                {isDescriptionShort && (
                  <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: 14 }}>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <b>La descripción del cargo es muy breve ({descriptionLength} caracteres).</b><br />
                    <span>
                      Para publicar en Trabajando.cl la descripción debe tener al menos <b>150 caracteres</b>.<br />
                      Edita la descripción en la publicación original del curso.
                    </span>
                  </div>
                )}
                <Card>
                  <CardBody>{description}</CardBody>
                </Card>
              </div>
              <div className="publication_requirements_box mb-4" id="publication_requirements_box">
                <small className="d-block text-muted fw-bold">
                  Requisitos
                </small>
                <Card>
                  <CardBody>{requirement}</CardBody>
                </Card>
              </div>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col lg="12" md="12">
              <Card className="mb-2 border-0 bg-light" id="publication_requirements">
                <CardBody>
                  <h3 className="h6 fw-bold">
                    <i className="fas fa-list-ul me-2"></i>
                    Requerimientos del Cargo
                  </h3>
                  <span className="d-block">
                    <strong>Experiencia:&nbsp;</strong>
                    {isPreview
                      ? trabajandoPublication.experienceYears?.value
                      : trabajandoPublication.experienceYears}{" "}
                    años.
                  </span>
                  <span className="d-block">
                    <strong>Perfíl Académico:&nbsp;</strong>
                    {trabajandoPublication.applicantProfile
                      ? trabajandoPublication.applicantProfile.name
                      : null}
                  </span>
                  <span className="d-block">
                    <strong>Nivel Académico:&nbsp;</strong>
                    {trabajandoPublication.educationLevel
                      ? trabajandoPublication.educationLevel.name
                      : null}
                  </span>
                  <span className="d-block">
                    <strong>Estado Académico:&nbsp;</strong>
                    {trabajandoPublication.educationState
                      ? trabajandoPublication.educationState.name
                      : null}
                  </span>
                  <span className="d-block">
                    <strong>Manejo de Software:&nbsp;</strong>
                    {trabajandoPublication.softwareLevel
                      ? trabajandoPublication.softwareLevel.name
                      : null}
                  </span>
                </CardBody>
              </Card>
              <Card className="mb-2 border-0 bg-light" id="publication_benefits">
                <CardBody>
                  <h3 className="h6 fw-bold">
                    <i className="far fa-smile-wink me-2"></i>
                    Beneficios
                  </h3>
                  {benefits}
                  <strong>Tipo de Contrato: </strong>
                  {course.contractType?.label}
                  <br />
                  {(isPreview &&
                      trabajandoPublication.showSalaryOffered?.value === true) ||
                    (!isPreview && trabajandoPublication.showSalaryOffered && (
                      <Fragment>
                        <strong>Sueldo Líquido: </strong>
                        {isPreview
                          ? trabajandoPublication.salaryOffered?.value
                          : trabajandoPublication.salaryOffered}
                      </Fragment>
                    ))}
                </CardBody>
              </Card>
              <Card className="mb-2 border-0 bg-light" id="publication_company_data">
                <CardBody>
                  <h3 className="h6 fw-bold">
                    <i className="fas fa-briefcase me-2"></i>
                    Datos de la Empresa
                  </h3>
                  <span className="d-block">
                    <strong>Actividad de la empresa:&nbsp;</strong>
                    {trabajandoPublication.companyActivity
                      ? trabajandoPublication.companyActivity.name
                      : null}
                  </span>
                  <span className="d-block">
                    <strong>Área:&nbsp;</strong>
                    {trabajandoPublication.area
                      ? trabajandoPublication.area.name
                      : null}
                  </span>
                  <span className="d-block">
                    <strong>Tipo de Trabajo:&nbsp;</strong>
                    {trabajandoPublication.jobType
                      ? trabajandoPublication.jobType.name
                      : null}
                  </span>
                  <br />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <div className="mt-4 text-center d-flex flex-wrap justify-content-center align-items-center">
            {renderButtons()}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default PublicationShow;
